import type { DataProvider, Identifier, RaRecord } from "ra-core";

/** A configured lead status value: a system one or an owner-added slug (spec §1). The live list: useLeadStatuses(). */
export type ProspectStatus = string;

export const CALL_OUTCOMES = [
  "no_answer",
  "voicemail",
  "talked_callback",
  "demo_booked",
  "brochure_sent",
  "not_interested",
  "wrong_number",
  "do_not_call",
  "unlogged",
  "skipped",
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

/** A row of companies_summary (spec 02 §2 + §6). */
export type Lead = RaRecord & {
  name: string;
  sector: string | null;
  trade: string | null;
  segment: string | null;
  fit_score: number | null;
  high_quality: boolean;
  estimated_employees: string | null;
  phone_number: string | null;
  phone_e164: string | null;
  phone_valid: boolean;
  source_id: string | null;
  source_query: string | null;
  date_added: string | null;
  assigned_sales_id: Identifier | null;
  prospect_status: ProspectStatus;
  last_call_outcome: CallOutcome | null;
  last_called_at: string | null;
  next_callback_at: string | null;
  attempt_count: number;
  cooldown_until: string | null;
  locked_by: Identifier | null;
  locked_until: string | null;
  language: "fr" | "en";
  email: string | null;
  website: string | null;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  region: string | null;
  /** companies.timezone (not null in the DB; may be missing on old FakeRest rows). */
  timezone?: string | null;
  state_abbr: string | null;
  description: string | null;
  tags: Identifier[];
  uid: string;
  updated_at: string;
  nb_contacts?: number;
  nb_deals?: number;
  assigned_rep_name: string | null;
  open_deal_count: number;
  last_email_at: string | null;
  next_task_due: string | null;
  is_phone_suppressed: boolean;
  is_locked: boolean;
  is_dnc: boolean;
  /** GAVION test mode: companies.is_test (admin-only; absent on old FakeRest rows = false). */
  is_test?: boolean;
  /** GAVION ops-lookup: « Responsable des opérations » (96_; absent on old FakeRest rows). */
  ops_contact_name?: string | null;
  ops_contact_role?: string | null;
  ops_contact_source_url?: string | null;
  ops_contact_found_at?: string | null;
  ops_contact_status?: "found" | "not_found" | "manual" | null;
  /** GAVION ops-lookup: whether a search can still answer (companies_summary; null = never searched or eligible). */
  ops_lookup_state?: "pending" | "later" | "given_up" | "done" | null;
  has_email: boolean;
  is_assigned: boolean;
  has_callback: boolean;
  queue_eligible: boolean;
  queue_order: string | null;
};

export type CompanyNoteSource =
  | "manual"
  | "import"
  | "call"
  | "granola"
  | "automation";
export type CompanyNote = RaRecord & {
  company_id: Identifier;
  text: string | null;
  date: string;
  sales_id: Identifier | null;
  status: string | null;
  source: CompanyNoteSource;
};

export type ConsentBasis =
  | "express"
  | "implied_conspicuous_publication"
  | "implied_existing_relationship"
  | "implied_inquiry"
  | "none";
export type Consent = RaRecord & {
  email: string;
  company_id: Identifier | null;
  contact_id: Identifier | null;
  basis: ConsentBasis;
  evidence: string | null;
  obtained_at: string;
  expires_at: string | null;
};

export type LeadImportOptions = {
  assigned_sales_id: number | null;
  tag_id: number | null;
  duplicate_policy: "skip" | "fill_blanks";
};
/**
 * One rejected row (`row` = the `_row` echoed back). A `possible_duplicate` (88_) also names the lead it looks like:
 * the lowest matching id, how many others match, and `match_row` when it is an earlier row of the same call (then
 * `match_id` is the lead that row created, null if it failed).
 */
export type LeadImportRejection = {
  row: number;
  reason: string;
  match_id?: number | null;
  match_name?: string | null;
  match_city?: string | null;
  match_reasons?: ("name_city" | "website")[];
  match_others?: number;
  match_row?: number;
};
export type LeadImportResult = {
  imported: number;
  updated: number;
  rejected: LeadImportRejection[];
};
/** The admin's decision on a possible duplicate, sent back with the row (`_duplicate`, `_match_id`). */
export type LeadImportDuplicateDecision = "create" | "fill";

/** Custom dataProvider methods added in batch 1 (both providers implement them, Task 11). */
export interface LeadDataProviderMethods {
  importCompanies(
    rows: Record<string, unknown>[],
    options: LeadImportOptions,
  ): Promise<LeadImportResult>;
  checkLeadPhones(
    phones: string[],
  ): Promise<{ existing: string[]; suppressed: string[] }>;
  assignLeads(ids: Identifier[], salesId: Identifier | null): Promise<number>;
  markDoNotCall(ids: Identifier[]): Promise<number>;
  /** GAVION call-brief-pipeline (84 §2): « Envoyer dans le pipeline »; the deal id (the lead's open deal when it has one). */
  sendToPipeline(
    companyId: Identifier,
    stage: string,
    at: string | null,
  ): Promise<number>;
}
export type LeadDataProvider = DataProvider & LeadDataProviderMethods;
